/**
 * Scan Linkage Widget (issue #2000, ADR-2000 decision 5)
 *
 * Per-team "% of Services linked to a repository scan". Teams with the lowest
 * linkage lead the list so the teams most in need of onboarding are the first
 * thing a user sees. Each row deep-links to that team's unlinked Services.
 * Scope resolution is server-side (same `my-teams` / `my-division` / `all`
 * rules as the Services by Lifecycle widget).
 */

import { Link } from '@tanstack/react-router';
import { useScanLinkage } from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { useStatisticsWidget } from './useStatisticsWidget';
import { SCANNER_ONBOARDING_URL } from '../../lib/scanManagedFields';
import type { WidgetConfig } from './types';
import '@tylertech/forge/icon';
import './ScanLinkageWidget.css';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'scan-linkage',
  title: 'Repository Scan Linkage',
  size: 'medium',
  refreshInterval: 600000, // 10 minutes, matching the sibling division-scoped widgets
  showRefresh: true,
  showExpand: false,
};

/** Rows shown before the list is cut off; the lowest-linkage teams come first. */
const MAX_TEAM_ROWS = 8;

type ScanLinkageStats = NonNullable<ReturnType<typeof useScanLinkage>['data']>['scanLinkage'];

function extractScanLinkageStats(
  data: ReturnType<typeof useScanLinkage>['data']
): ScanLinkageStats | null {
  return data?.scanLinkage ?? null;
}

export function ScanLinkageWidget(): JSX.Element {
  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    useScanLinkage(),
    extractScanLinkageStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const IconElement = 'forge-icon' as React.ElementType;

  if (!stats) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="scan-linkage-widget__empty">
          <IconElement name="link" className="scan-linkage-widget__empty-icon" />
          <p className="forge-typography--body2">No scan linkage data available</p>
        </div>
      </DashboardWidget>
    );
  }

  const visibleTeams = stats.teams.slice(0, MAX_TEAM_ROWS);
  const hiddenCount = stats.teams.length - visibleTeams.length;

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      <div className="scan-linkage-widget__content">
        <p
          className="scan-linkage-widget__scope-label forge-typography--caption"
          data-testid="scan-linkage-scope"
        >
          Scope: {stats.scope}
        </p>

        <div className="scan-linkage-widget__total">
          <div className="scan-linkage-widget__total-number" data-testid="scan-linkage-percentage">
            {stats.percentage}%
          </div>
          <div className="scan-linkage-widget__total-label forge-typography--label2">
            {stats.linked} of {stats.total} Services linked to a repository scan
          </div>
        </div>

        {visibleTeams.length === 0 ? (
          <p className="forge-typography--body2">No services in this scope</p>
        ) : (
          <ul className="scan-linkage-widget__list" data-testid="scan-linkage-list">
            {visibleTeams.map((team) => (
              <li key={team.teamId ?? 'unassigned'} className="scan-linkage-widget__row">
                {team.teamId ? (
                  <Link
                    to="/services"
                    search={{
                      page: 1,
                      pageSize: 25,
                      search: '',
                      lifecycleStatus: '',
                      tier: '',
                      tagId: '',
                      teamId: team.teamId,
                      technologyId: '',
                      scanLinked: 'unlinked',
                    }}
                    className="scan-linkage-widget__team"
                  >
                    {team.teamName}
                  </Link>
                ) : (
                  <span className="scan-linkage-widget__team">{team.teamName}</span>
                )}
                <div className="scan-linkage-widget__bar-track">
                  <div
                    className="scan-linkage-widget__bar-fill"
                    style={{ width: `${team.percentage}%` }}
                  />
                </div>
                <span className="scan-linkage-widget__count forge-typography--label1">
                  {team.linked}/{team.total} ({team.percentage}%)
                </span>
              </li>
            ))}
          </ul>
        )}

        {hiddenCount > 0 && (
          <p className="forge-typography--caption scan-linkage-widget__more">
            and {hiddenCount} more {hiddenCount === 1 ? 'team' : 'teams'}
          </p>
        )}

        <a
          className="scan-linkage-widget__guide forge-typography--caption"
          href={SCANNER_ONBOARDING_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          How to link a Service to a repository scan
        </a>
      </div>
    </DashboardWidget>
  );
}
